import React, { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import get from 'lodash/get';
import { IFieldComponentProps, Input } from '../../ComposableField';
import { useComposableForm } from '../../ComposableForm';
import { parseDependentFieldId, isCRMAccountSearcherDependentField } from '../CRMFieldUtils';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { getAccountById } from 'src/screens/Profile/CRMIntegration';

/**
 * Wrapper around Input that auto-populates from CRM account data when parent field is a CRM account selector.
 */
const CRMInput: React.FC<IFieldComponentProps<string>> = (props) => {
  const { field, onChange, values } = props;
  const { rhForm, customFields } = useComposableForm();
  const { canUseMeetingCRMFields } = useCRMStatus();

  const parsedDependency = useMemo(() => {
    if (!field.dependentFieldId) return null;
    return parseDependentFieldId(field.dependentFieldId);
  }, [field.dependentFieldId]);

  const parentField = useMemo(() => {
    if (!parsedDependency) return undefined;
    return customFields.find((cf) => cf.id === parsedDependency.parentFieldId);
  }, [parsedDependency, customFields]);

  const isCRMField = useMemo(() => {
    return isCRMAccountSearcherDependentField(field, parentField);
  }, [field, parentField]);

  const parentAccountId = useWatch({
    control: rhForm.control,
    name: parsedDependency?.parentFieldId || '',
  }) as string | undefined;

  const subkey = useMemo(() => {
    return parsedDependency?.subkey || field.reportingName;
  }, [parsedDependency, field.reportingName]);

  const lastPopulatedAccountIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!canUseMeetingCRMFields || !isCRMField || !parentAccountId || !subkey) {
      if (!parentAccountId) {
        lastPopulatedAccountIdRef.current = undefined;
      }
      return;
    }

    if (lastPopulatedAccountIdRef.current === parentAccountId) {
      return;
    }

    const updateFieldFromAccount = async () => {
      const accountORM = await getAccountById(parentAccountId);
      if (!accountORM?.model) {
        lastPopulatedAccountIdRef.current = parentAccountId;
        rhForm.setValue(field.id, '', { shouldDirty: false });
        onChange('');
        return;
      }

      let fieldValue = get(accountORM.model, subkey) as string | undefined;
      if (!fieldValue && values?.[field.id]) {
        fieldValue = values[field.id].values[0];
      }
      const valueToSet = (fieldValue && typeof fieldValue === 'string') ? fieldValue : '';

      lastPopulatedAccountIdRef.current = parentAccountId;
      rhForm.setValue(field.id, valueToSet, { shouldDirty: false });
      onChange(valueToSet);
    };

    updateFieldFromAccount();
  }, [
    parentAccountId,
    subkey,
    isCRMField,
    canUseMeetingCRMFields,
    field.id,
    onChange,
    rhForm,
    field.dependentFieldId,
    field.fieldLabel,
  ]);

  return <Input {...props} />;
};

export default CRMInput;
